import { useRouteError } from 'react-router';

import type { BaseConfig, Logger } from '../../../';

import { ErrorBoundaryContent } from './ErrorBoundaryContent';

interface Props {
  config: BaseConfig;
  logger: Logger;
}

type ErrorObject = Error & {
  graphQLErrors: unknown[];
  networkError: {
    result: unknown[];
  };
  status: number;
  cause?: {
    result?: string;
    statusCode?: number;
  };
};

const logForErrorBoundary = (logger: Logger, error: ErrorObject) => {
  const routeError = {
    error,
    graphQLErrorsStringified: JSON.stringify(error.graphQLErrors || []),
    networkError: {
      ...error.networkError,
      result: JSON.stringify(error.networkError?.result || []),
    },
  };

  // If its a 400 and result: Query request complexity limit has been reached. Then we don't want to log it as an error
  if (
    error.cause?.statusCode === 400 &&
    typeof error?.cause?.result?.includes === 'function' &&
    error.cause.result.includes(
      'Query request complexity limit has been reached.',
    )
  ) {
    logger.warn(
      {
        ...routeError,
      },
      'ErrorBoundary caught an warning',
    );

    return;
  }

  // If its a 404 response, we don't want to log it as an error
  // If its a 429 response, we don't want to log it as an error as this is a potential DDOS attack or scraping that candidate graph is blocking
  if (
    ![404, 429].some((code) =>
      [error.status, error.cause?.statusCode].includes(code),
    )
  ) {
    logger.error({ ...routeError }, 'ErrorBoundary caught an error');
  }
};

export function ErrorBoundaryContainer({ logger, config }: Props) {
  const error = useRouteError() as ErrorObject;

  logForErrorBoundary(logger, error);

  return <ErrorBoundaryContent config={config} />;
}
